import React, { useState, useEffect } from "react"
import { Col, Row } from "reactstrap"
import { useParams } from "react-router-dom"
import toast from "react-hot-toast"
import { DragDropContext, Droppable } from "react-beautiful-dnd"

import editIcon from "../../../assets/images/icons/edit-icon.svg"
import eyeIcon from "../../../assets/images/icons/eye-icon.svg"
import DeleteIcon from "assets/images/icons/finance-vendor-mang-del-icon.svg"

import ActiveInactiveSwitch from "../CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import { DataTable } from "../../../components/DataTable"
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest"
import { Pagination } from "../../../components/Pagination"
import {
   UpdateTrailerEquipmentSequence,
   GetTrailerEquipmentItems,
   UpdateTrailerEquipmentItem,
   DeleteTrailerEquipmentItem,
   GetEquipmentsCategoryItemsListing,
   GetTrailersDetails,
} from "../../../api/api.service"
import { SearchBox } from "../../../components/SearchBox"
import { Button } from "../../../components/Button"
import { AutoComplete } from "../../../components/AutoComplete"
import {
   EquipmentCategoriesStatus,
   EquipmentCategoryItemsStatus,
} from "../../../constants"
import { convertToSelectOptions, withOrderSaveGuard } from "../../../utils/commonUtils"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import DeleteModal from "../CommonUI/Modal/DeleteModal"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import useAsyncSelect from "hooks/useAsyncSelect"
import ManageImportGlobalEquipmentModal from "./ManageImportGlobalEquipmentModal"
import TruncatedTextWithTooltip from "components/CustomTooltip/CustomTooltip"
import ManageEquipmentsCategoryItems from "./ManageEquipmentsCategoryItems"

const COLUMNS = {
   EQUIPMENT_NAME: "Equipment Name",
   PRICE: "Price",
   DATE_EDITED: "Date edited",
   STATUS: "Status",
   ACTIONS: "Actions",
}

const DATA_KEY = "data"
const TOTAL_KEY = "total"

const Equipment = ({}) => {
   const { trailerId } = useParams()
   // Equipment reorder is locked unless the trailer is in custom order (flag is trailer-wide).
   const [orderCustomized, setOrderCustomized] = useState(false)
   useEffect(() => {
      let cancelled = false
      GetTrailersDetails(trailerId)
         .then(res => {
            if (!cancelled) setOrderCustomized(Boolean(res?.orderCustomized))
         })
         .catch(() => {})
      return () => {
         cancelled = true
      }
   }, [trailerId])
   const [statusFilters, setStatusFilters] = useState(null)
   const [selection, setSelection] = useState({})
   const [openDelModal, setOpenDelModal] = useState(false)
   const [selectedEquipment, setSelectedEquipment] = useState(null)
   const [openSelectEquipmentsModal, setOpenSelectEquipmentsModal] =
      useState(false)
   const [selectedEquipments, setSelectedEquipments] = useState([])
   const [eventsType, setEventsType] = useState({
      isManage: null,
      isUpdate: null,
      isDetails: null,
   })

   // For Global Equipment Categories selection
   const {
      setSelectedOption: setSelectedGlobalEquipmentItemsOption,
      selectEntity: globalEquipmentsItemsOptions,
      setSelectEntity: setGlobalEquipmentsItemsOptions,
      handleSearchOnOptions: handleSearchGlobalEquipmentItems,
      selectedOption: selectedGlobalEquipmentItemsOption,
      handleSelectAOption: handleSelectAGlobalEquipmentItemsOption,
   } = useAsyncSelect({
      apiFunc: GetEquipmentsCategoryItemsListing,
      labelKey: "name",
      valueKey: "id",
      restrictExecutionOnRender: false,
      params: {
         limit: -1,
      },
      onOptionSelect: option => {
         setSelectedEquipment(option)
      },
   })

   // For Local Equipments Items Listing

   const {
      data: equipmentItemsData,
      isFetching: equipmentItemsIsFetching,
      page: equipmentItemsPage,
      searchText: equipmentItemsSearchText,
      pageSize: equipmentItemsPageSize,
      total: equipmentItemsTotal,
      handleSearchChange: handleEquipmentItemsSearchChange,
      request: equipmentItemsRequest,
      handlePageClick: handleEquipmentItemsPageClick,
      onChangePageSize: handleEquipmentItemsChangePageSize,
      setTableDataChangeLoading: setEquipmentItemsTableDataChangeLoading,
      tableDataChangeLoading: equipmentItemsTableDataChangeLoading,
      setData: setEquipmentItemsData,
   } = useLocalPaginatedRequest({
      requestFn: GetTrailerEquipmentItems,
      customParams: {
         trailerId: Number(trailerId),
         column: "sequenceNo",
         limit: -1,
         page: 1,
      },
      deps: [statusFilters],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   const handleInitManage = (obj = null) => {
      setEventsType(prev => ({ ...prev, isManage: true }))
      setSelection(obj)
   }
   const handleSelectStatusOption = option => {
      setStatusFilters(option)
   }

   const handleStatusChange = async (currStatus, id) => {
      let status =
         currStatus === EquipmentCategoryItemsStatus.Active
            ? EquipmentCategoryItemsStatus.Inactive
            : EquipmentCategoryItemsStatus.Active
      let dataClone = [...equipmentItemsData]
      dataClone.map(el => {
         if (el?.id == id) el.status = status
      })
      setEquipmentItemsData(dataClone)
      let payload = {
         status: status,
      }
      try {
         setEquipmentItemsTableDataChangeLoading(true)
         let res = await UpdateTrailerEquipmentItem(id, payload)
         toast.success("Item status has been updated !")
      } catch (err) {
         toast.error(err.message)
         let dataClone = [...equipmentItemsData]
         dataClone.map(el => {
            if (el?.id == id) el.status = currStatus
         })
         setEquipmentItemsData(dataClone)
      } finally {
         setEquipmentItemsTableDataChangeLoading(false)
      }
   }
   const handleInitDelete = obj => {
      setSelection(obj)
      setOpenDelModal(prev => !prev)
   }

   const handleInitViewDetails = (obj = null) => {
      setSelection(obj)
      setEventsType(prev => ({ ...prev, isDetails: true, isManage: true }))
   }

   const handleDragEnd = async result => {
      if (!orderCustomized) return
      if (statusFilters?.value) return
      if (!result.destination) return

      const items = Array.from(equipmentItemsData)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      setEquipmentItemsData(items)

      let payload = {
         trailerId: Number(trailerId),
         sequenceItems: items.map((trailerObj, ind) => ({
            id: Number(trailerObj.id),
            sequenceNo: ind,
         })),
      }

      try {
         await withOrderSaveGuard(() => UpdateTrailerEquipmentSequence(payload))
         toast.success("Sequence updated successfully!")
      } catch (err) {
         toast.error("Failed to update sequence")
      }
   }
   return (
      <>
         <div className="mb-3 trailer-container">
            <div className="d-flex align-items-center justify-content-end admin-inventory-management-general-inventory-responsiveness trailer-btn-container">
               <Button
                  className={`header-button headerButtonResponsive global-selection-button`}
                  title="Select Equipment(s)"
                  onClick={() => setOpenSelectEquipmentsModal(true)}
               />

               <Button
                  className={`header-button headerButtonResponsive`}
                  title="Add Equipment"
                  onClick={() => handleInitManage({})}
               />
            </div>
         </div>

         <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness ">
               <div className="status-search-con wh-im-dpgi-status  me-2 ">
                  <AutoComplete
                     value={statusFilters}
                     isClearable
                     onChange={val => handleSelectStatusOption(val)}
                     isSearchable={false}
                     placeholder="Select status"
                     customStyles={autoCompleteStyles.STYLE_SIX}
                     classNamePrefix="status-header-search-admin"
                     options={convertToSelectOptions(EquipmentCategoriesStatus)}
                  />
               </div>
               <div className="ad-location-search-box">
                  <SearchBox
                     inputClasses="vendor-search-input input-search"
                     labelClass="d-none"
                     placeholder="Search"
                     searchIcon={true}
                     searchIconClass="custom-input-search-icon"
                     conClass={`search-input-container-class`}
                     searchText={equipmentItemsSearchText}
                     onChange={handleEquipmentItemsSearchChange}
                  />
               </div>
            </div>
         </div>
         <Row>
            <Col md={12}>
               <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="droppable">
                     {provided => (
                        <div
                           {...provided.droppableProps}
                           ref={provided.innerRef}
                        >
                           <DataTable
                              isDraggable={orderCustomized && !statusFilters?.value}
                              droppablePlaceholder={provided.placeholder}
                              data={equipmentItemsData}
                              tableClasses="equipment-category-table"
                              loading={equipmentItemsIsFetching}
                              config={[
                                 {
                                    title: COLUMNS.EQUIPMENT_NAME,
                                    className: "",
                                    render: data => (
                                       <TruncatedTextWithTooltip
                                          text={data.name}
                                       />
                                    ),
                                 },
                                 {
                                    title: COLUMNS.PRICE,
                                    className: "",
                                    render: data => "$" + data?.price || "-",
                                 },
                                 {
                                    title: COLUMNS.DATE_EDITED,
                                    className: "",
                                    render: data => {
                                       return convertDateTime({
                                          date: data.updatedAt,
                                          customFormat:
                                             dateTimeFormat.casitaCustomDateFormat,
                                          dateOnly: true,
                                       })
                                    },
                                 },

                                 {
                                    title: COLUMNS.STATUS,
                                    className: "",
                                    render: data => (
                                       <ActiveInactiveSwitch
                                          isDisable={
                                             equipmentItemsTableDataChangeLoading
                                          }
                                          id={data.id}
                                          name={`${data.id}`}
                                          onChange={e =>
                                             handleStatusChange(
                                                data?.status,
                                                data?.id
                                             )
                                          }
                                          isChecked={
                                             data?.status ==
                                             EquipmentCategoryItemsStatus.Active
                                          }
                                       />
                                    ),
                                 },

                                 {
                                    title: COLUMNS.ACTIONS,

                                    render: data => (
                                       <div className="d-flex gap-2 pointer">
                                          <img
                                             src={eyeIcon}
                                             onClick={() =>
                                                handleInitViewDetails(data)
                                             }
                                          />

                                          <img
                                             src={editIcon}
                                             onClick={() =>
                                                handleInitManage(data)
                                             }
                                          />
                                          <img
                                             src={DeleteIcon}
                                             onClick={() =>
                                                handleInitDelete(data)
                                             }
                                          />
                                       </div>
                                    ),
                                 },
                              ]}
                           />
                        </div>
                     )}
                  </Droppable>
               </DragDropContext>
            </Col>
         </Row>

         <ManageImportGlobalEquipmentModal
            isOpen={openSelectEquipmentsModal}
            onToggleModal={() => {
               setOpenSelectEquipmentsModal(false)
               setSelectedEquipments([])
            }}
            equipmentsOptions={globalEquipmentsItemsOptions}
            setEquipmentsOptions={setGlobalEquipmentsItemsOptions}
            handleSearchEquipment={handleSearchGlobalEquipmentItems}
            selectedEquipments={selectedGlobalEquipmentItemsOption}
            setSelectedEquipments={setSelectedGlobalEquipmentItemsOption}
            trailerId={trailerId}
            onSuccess={equipmentItemsRequest}
            existingTrailerEquipments={equipmentItemsData || []}
         />

         {Object.values(eventsType).includes(true) && (
            <ManageEquipmentsCategoryItems
               isUpdating={Boolean(eventsType.isManage && selection)}
               isDetailView={eventsType.isDetails}
               isOpen={eventsType.isManage}
               id={selection?.id}
               onSuccess={equipmentItemsRequest}
               onToggleModal={() => {
                  setSelection(null)
                  setEventsType(prev => ({
                     ...prev,
                     isManage: false,
                     isDetails: false,
                  }))
               }}
            />
         )}
         <DeleteModal
            isOpen={openDelModal}
            toggleFunction={setOpenDelModal}
            id={selection?.id}
            apiFunction={DeleteTrailerEquipmentItem}
            title={"Equipment Item"}
            requestFunction={equipmentItemsRequest}
         />
      </>
   )
}

export default Equipment
